import { useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { cn } from '../../lib/utils';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowsRightLeftIcon,
  ArrowUturnRightIcon,
  EllipsisHorizontalCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

/**
 * The seven kinds of design a Workflow node can hold.
 * These map 1:1 to the worker's aiImageRoute typePrompts.
 */
export type PlanNodeType =
  | 'concept'
  | 'floor_plan'
  | 'site_plan'
  | 'elevation'
  | 'section'
  | 'presentation'
  | 'working_drawing';

export const NODE_TYPE_LABEL: Record<PlanNodeType, string> = {
  concept: 'Concept',
  floor_plan: 'Floor Plan',
  site_plan: 'Site Plan',
  elevation: 'Elevation',
  section: 'Section',
  presentation: 'Presentation',
  working_drawing: 'Working Drawing',
};

export const ALL_NODE_TYPES: PlanNodeType[] = [
  'concept',
  'floor_plan',
  'site_plan',
  'elevation',
  'section',
  'presentation',
  'working_drawing',
];

export interface PlanNodeData extends Record<string, unknown> {
  title?: string;
  imageUrl?: string;
  nodeType: PlanNodeType;
  status: 'idle' | 'generating' | 'error';
  errorMessage?: string;
  /** Raw user text that created this node (~40 chars shown as title). */
  prompt?: string;
  /** Full brief sent to the AI — includes root brief + any refinements. */
  subjectBrief?: string;
  /**
   * When true, this node's image is used as the visual anchor for any
   * NEW top-level generation from the docked composer, no matter where
   * on the canvas the user has clicked. Only one node at a time carries
   * this flag; toggling it on another moves it. Chained spawns and
   * refinements still use the direct parent image.
   */
  isReference?: boolean;

  onDownload?: () => void;
  onExpand?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onReference?: () => void;
  onCopyImage?: () => void;
  onRetry?: () => void;

  /** Called when the user picks a type from the connector dropdown. */
  onSpawnChild?: (nextType: PlanNodeType) => void;
  /**
   * Called when the user types a short refinement into the mini composer
   * and hits Enter. Spawns a child of the same type using the parent's
   * brief + this refinement + the parent image as visual reference.
   */
  onRefine?: (refinement: string) => void;
}

/**
 * A Workflow canvas node. Renders one of three visual states:
 *   idle       — image card with action bar under it (on select)
 *   generating — peach placeholder while the AI is thinking
 *   error      — red border + retry button
 * The right-edge connector is a button that opens a dropdown of node types
 * so the user can spawn a next-step design (elevation, floor plan, etc.)
 * from the current one.
 */
export default function PlanNode({ data, selected }: NodeProps) {
  const d = data as PlanNodeData;
  const [menuOpen, setMenuOpen] = useState(false);
  const [connectorOpen, setConnectorOpen] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineText, setRefineText] = useState('');
  const refineInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (refineOpen) refineInputRef.current?.focus();
  }, [refineOpen]);

  // Collapse the mini composer whenever the node is deselected.
  useEffect(() => {
    if (!selected) {
      setRefineOpen(false);
      setRefineText('');
    }
  }, [selected]);

  const submitRefinement = () => {
    const value = refineText.trim();
    if (!value || !d.onRefine) return;
    d.onRefine(value);
    setRefineText('');
    setRefineOpen(false);
  };

  const isGenerating = d.status === 'generating';
  const isError = d.status === 'error';

  return (
    <div className="relative flex flex-col items-start font-display">
      {/* Type badge + optional reference marker */}
      <div className="mb-1 flex items-center gap-1">
        <div className="rounded-md bg-background/95 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground shadow-sm backdrop-blur">
          {NODE_TYPE_LABEL[d.nodeType]}
        </div>
        {d.isReference && (
          <div
            className="rounded-md bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary shadow-sm"
            title="Anchor for new top-level generations"
          >
            Reference
          </div>
        )}
      </div>

      {/* Card */}
      <div
        className={cn(
          'relative overflow-hidden rounded-md transition-shadow',
          selected ? 'ring-2 ring-primary/60 ring-offset-2 ring-offset-background' : 'ring-1 ring-border',
          isGenerating && 'bg-[#fddfd0]',
          isError && '!ring-2 !ring-destructive',
        )}
        style={{ width: 220, height: 220 }}
      >
        {isGenerating && (
          <div className="flex h-full w-full items-center justify-center text-primary/60">
            <ArrowPathIcon className="h-6 w-6 animate-spin" />
          </div>
        )}
        {isError && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center text-destructive">
            <ExclamationTriangleIcon className="h-6 w-6" />
            <p className="text-xs">{d.errorMessage || 'Generation failed'}</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); d.onRetry?.(); }}
              className="rounded bg-destructive/10 px-2 py-1 text-xs hover:bg-destructive/20"
            >
              Retry
            </button>
          </div>
        )}
        {!isGenerating && !isError && d.imageUrl && (
          <img
            src={d.imageUrl}
            alt={d.title || NODE_TYPE_LABEL[d.nodeType]}
            className="h-full w-full object-cover"
            draggable={false}
          />
        )}
      </div>

      {/* Invisible source handle so ReactFlow can still draw connections
          into this node's right edge. Clicking is handled by the overlay
          button beneath — Handle swallows onClick, so we can't use it. */}
      <Handle
        type="source"
        position={Position.Right}
        className="!h-1 !w-1 !border-none !bg-transparent !opacity-0"
        style={{ right: -1, top: 'calc(50% - 100px)' }}
      />
      <Handle type="target" position={Position.Left} className="!opacity-0" />

      {/* Visible connector button — click to open the type dropdown. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!isGenerating && d.onSpawnChild) setConnectorOpen((v) => !v);
        }}
        disabled={isGenerating}
        aria-label="Generate next node"
        className={cn(
          'nodrag nopan absolute z-10 flex h-7 w-7 items-center justify-center rounded-full bg-primary/20 shadow-sm transition-transform hover:scale-110 disabled:opacity-40',
          'top-1/2 -translate-y-1/2',
        )}
        style={{ right: -14, top: 'calc(50% + 12px)' }}
      >
        <ArrowsRightLeftIcon className="h-4 w-4 text-primary" />
      </button>

      {connectorOpen && (
        <div
          className="nodrag nopan absolute z-30 flex w-44 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-md"
          style={{ left: 240, top: 40 }}
        >
          <p className="border-b border-border px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Generate from this
          </p>
          {ALL_NODE_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                d.onSpawnChild?.(t);
                setConnectorOpen(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
            >
              {NODE_TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      )}

      {/* Action bar under the card, shown when node is selected */}
      {selected && !isGenerating && (
        <div className="nodrag nopan mt-2 flex flex-col items-start gap-2">
          <div className="relative flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 shadow-sm">
            <NodeAction
              icon={SparklesIcon}
              label="Refine"
              onClick={() => setRefineOpen((v) => !v)}
              active={refineOpen}
            />
            <NodeAction icon={ArrowDownTrayIcon} label="Download" onClick={d.onDownload} />
            <NodeAction icon={ArrowsPointingOutIcon} label="Expand" onClick={d.onExpand} />
            <NodeAction icon={TrashIcon} label="Delete" onClick={d.onDelete} />
            <NodeAction
              icon={EllipsisHorizontalCircleIcon}
              label="More"
              onClick={() => setMenuOpen((v) => !v)}
            />

            {menuOpen && (
              <div className="absolute left-0 top-full z-30 mt-1 flex w-52 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-md">
                <MenuItem label="Copy Image" onClick={() => { d.onCopyImage?.(); setMenuOpen(false); }} />
                <MenuItem label="Duplicate" onClick={() => { d.onDuplicate?.(); setMenuOpen(false); }} />
                <MenuItem label="Delete Node" onClick={() => { d.onDelete?.(); setMenuOpen(false); }} />
                <MenuItem
                  label={d.isReference ? 'Unset as Reference' : 'Set as Reference'}
                  onClick={() => { d.onReference?.(); setMenuOpen(false); }}
                />
              </div>
            )}
          </div>

          {refineOpen && (
            <div className="flex w-[280px] items-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 shadow-sm">
              <input
                ref={refineInputRef}
                value={refineText}
                onChange={(e) => setRefineText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); submitRefinement(); }
                  if (e.key === 'Escape') { setRefineOpen(false); setRefineText(''); }
                }}
                placeholder="Add balconies, warmer light…"
                aria-label="Refine this design"
                className="min-w-0 flex-1 border-0 bg-transparent px-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); submitRefinement(); }}
                disabled={!refineText.trim()}
                aria-label="Generate refinement"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-30"
              >
                <ArrowUturnRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NodeAction({
  icon: ActionIcon,
  label,
  onClick,
  active,
}: {
  icon: IconComponent;
  label: string;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
        active
          ? 'bg-primary/15 text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <ActionIcon className="h-4 w-4" />
    </button>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="w-full px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
    >
      {label}
    </button>
  );
}
